import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import AuthCard from '@/components/auth/AuthCard';
import { userManagementService } from '@/api/services/UserManagementService';
import { useAuth } from '@/stores/authContext';
import { ROUTES } from '@/routes/paths';

type VerifyStatus = 'loading' | 'success' | 'error';

// Технический экран: подтверждает email по токену из письма. Перед запросом чистим
// возможную протухшую сессию (skipAuthRefresh не даёт интерсептору вмешаться). Успех →
// сохраняем токены и уходим в /app; ошибка → сообщение + ссылка на вход.
export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { loginWithTokens, logout } = useAuth();

  const [status, setStatus] = useState<VerifyStatus>('loading');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    if (!id) {
      setStatus('error');
      return;
    }

    const isInvitation = location.pathname.includes('/invitation/');
    logout(); // очистить старую сессию ДО запроса, чтобы не было каскада 401

    const request = isInvitation
      ? userManagementService.verifyInvitationEmail(id)
      : userManagementService.verifyEmail(id);
    request
      .then((tokens) => {
        loginWithTokens(tokens);
        setStatus('success');
        navigate(ROUTES.APP, { replace: true });
      })
      .catch(() => {
        setStatus('error');
      });
  }, [id, location.pathname, loginWithTokens, logout, navigate]);

  if (status === 'error') {
    return (
      <AuthCard title={t('auth.verifyEmail.errorTitle')} description={t('auth.verifyEmail.errorText')}>
        <Button component={Link} to={ROUTES.LOGIN} fullWidth>
          {t('auth.verifyEmail.toLogin')}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.verifyEmail.title')}>
      <Center py="md">
        {status === 'loading' ? (
          <Group gap="sm">
            <Loader size="sm" />
            <Text c="gray.7">{t('auth.verifyEmail.loading')}</Text>
          </Group>
        ) : (
          <Stack align="center" gap="xs">
            <IconCircleCheck size={28} color="var(--mantine-color-brand-7)" aria-hidden />
            <Text c="gray.7">{t('auth.verifyEmail.success')}</Text>
          </Stack>
        )}
      </Center>
    </AuthCard>
  );
}
